import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { actionsMetaQuery, capabilitiesQuery, type ActionsMeta, type TrackFieldInfo } from '@/api/actions'

/**
 * The manual editor's vocabulary: what `GET /api/actions/meta` says (derived on the server from the
 * real models and interpreter constants), laid over built-in defaults that mirror
 * `backend/app/story/actions/models.py` so the editor still works against a server without the
 * endpoint. Every list the server sends wins over the default of the same name.
 */

const TRACK_FIELDS: TrackFieldInfo[] = [
  { name: 'clip', type: 'clip', help: 'A move to play: wave, jump, throw…' },
  { name: 'face', type: 'ref', help: 'Turn toward someone or something (or left, right, camera).' },
  { name: 'loops', type: 'number', help: 'How many times the move plays.' },
  { name: 'move', type: 'object', help: 'Walk, fly or swim somewhere first.' },
  { name: 'layer', type: 'object', help: 'Play the move with the upper body only, so legs keep walking.' },
  { name: 'mask', type: 'object', help: 'Which body parts the move uses (upper, head, hold…).' },
  { name: 'attach', type: 'object', help: 'Is put into a hand, a mouth, on a head or a back.' },
  { name: 'detach', type: 'object', help: 'Is let go (and may fall to the ground).' },
  { name: 'toss', type: 'object', help: 'Flies through the air from one place to another.' },
  { name: 'mount', type: 'ref', help: 'Climbs onto someone or something to ride it.' },
  { name: 'dismount', type: 'object', help: 'Gets off what it was riding.' },
  { name: 'vanish', type: 'bool', help: 'Disappears from the scene.' },
  { name: 'appear', type: 'object', help: 'Appears (somewhere).' },
  { name: 'react', type: 'object', help: 'A piece of scenery or a place reacts: a move and/or an effect.' },
  { name: 'sync', type: 'object', help: 'Times the move so its moment (contact, release…) meets another event.' },
  { name: 'spawn', type: 'object', help: 'New things appear, like apples falling from a shaken tree.' },
]

export const DEFAULT_META: ActionsMeta = {
  entity_types: ['character', 'object', 'scenery', 'feature', 'spot'],
  categories: ['play', 'social', 'locomotion', 'reaction', 'rest', 'twist', 'motion', 'effect'],
  phases: ['anticipate', 'action', 'settle', 'react'],
  clause_kinds: ['role', 'relation', 'stage', 'state', 'any', 'not'],
  relations: ['near', 'far', 'holding', 'held_by', 'on', 'inside', 'behind', 'taller_than', 'shorter_than', 'can_reach', 'same_region', 'in_region'],
  events: ['land', 'enter', 'leave', 'contact', 'drop', 'arrive', 'state_change'],
  stops: ['beside', 'at', 'behind', 'on', 'inside', 'edge', 'across'],
  toss_then: ['catch', 'land', 'vanish'],
  sockets: ['hand', 'other_hand', 'mouth', 'head', 'back', 'top'],
  fx_kinds: ['sparkle', 'hearts', 'stars', 'dust', 'splash', 'bubbles', 'confetti', 'zzz', 'notes', 'question', 'exclaim', 'crumbs', 'wind', 'magic', 'rainbow', 'snow', 'rain', 'leaves', 'petals', 'fireflies', 'twinkle', 'shine', 'glow', 'puff', 'ripple', 'shooting_star'],
  layers: ['upper'],
  masks: ['upper', 'upper_r', 'upper_l', 'upper_near', 'upper_far', 'head', 'hold', 'mouth', 'tail', 'wings', 'arms'],
  capabilities: ['holdable', 'throwable', 'kickable', 'rollable', 'bounces', 'edible', 'rideable', 'floats', 'flies', 'climbable', 'sittable', 'hideable_behind', 'shakeable', 'swimmable', 'floatable_in', 'enterable', 'openable', 'has_hands', 'has_mouth', 'can_swim', 'can_fly', 'can_climb'],
  clip_names: [
    'idle', 'move', 'talk', 'greet', 'happy', 'sad', 'surprised', 'jump', 'sit', 'dance', 'reach', 'pick_up', 'hold', 'throw', 'catch', 'kick', 'eat', 'swim',
    'fly', 'ride', 'hide', 'push', 'point', 'hug', 'sleep', 'roll', 'bounce', 'spin', 'float', 'fall', 'sway', 'drift', 'open', 'bob', 'flutter', 'shake',
  ], // prettier-ignore
  plans: ['humanoid', 'quadruped', 'bird', 'fish', 'serpent', 'insect', 'radial', 'blob', 'plant', 'prop'],
  kinds: ['person', 'animal', 'bird', 'fish', 'creature', 'object'],
  state_keys: ['pos', 'facing', 'holding', 'holder', 'held_in', 'mounted_on', 'carrying', 'inside', 'hidden_behind', 'open', 'wet', 'mood', 'visible', 'asleep'],
  track_fields: TRACK_FIELDS,
  moments: ['arrive', 'land', 'release', 'grab', 'contact'],
  reserved_ids: ['meta', 'validate', 'author'],
  ref_help:
    'A place or a participant: $name is the participant itself, $name.feet its feet, $name.top the top of it, ' +
    '$name.edge / $name.center / $name.surface for places, $name.socket.hand (or other_hand, mouth, head, back, top) a body part, ' +
    'and spot:left, spot:middle or spot:right a spot on the stage.',
}

type ListKey = { [K in keyof ActionsMeta]-?: ActionsMeta[K] extends string[] ? K : never }[keyof ActionsMeta]
const LIST_KEYS = Object.keys(DEFAULT_META).filter((k) => Array.isArray(DEFAULT_META[k as keyof ActionsMeta]) && k !== 'track_fields') as ListKey[]

const strings = (v: unknown): string[] | null => {
  if (!Array.isArray(v)) return null
  const out = [...new Set(v.filter((x): x is string => typeof x === 'string' && x.trim() !== ''))]
  return out.length > 0 ? out : null
}

/** The server's meta over the defaults; `extraCapabilities` (from the capability map) are added. */
export function mergeMeta(server: Partial<ActionsMeta> | null | undefined, extraCapabilities: readonly string[] = []): ActionsMeta {
  const out: ActionsMeta = { ...DEFAULT_META }
  const s = (server ?? {}) as Record<string, unknown>
  for (const k of LIST_KEYS) {
    const v = strings(s[k])
    if (v) out[k] = v
  }
  if (Array.isArray(s.track_fields)) {
    const fields = s.track_fields.filter((f): f is TrackFieldInfo => !!f && typeof f === 'object' && typeof (f as TrackFieldInfo).name === 'string')
    if (fields.length > 0)
      out.track_fields = fields.map((f) => ({
        name: f.name,
        type: f.type,
        help: typeof f.help === 'string' ? f.help : '',
        ...(typeof f.label === 'string' && f.label ? { label: f.label } : {}),
        ...(f.advanced === true ? { advanced: true } : {}),
        ...(Array.isArray(f.choices) ? { choices: f.choices.filter((c): c is string => typeof c === 'string') } : {}),
      }))
  }
  if (s.capability_help && typeof s.capability_help === 'object') {
    out.capability_help = Object.fromEntries(Object.entries(s.capability_help as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === 'string'))
  }
  if (typeof s.ref_help === 'string' && s.ref_help.trim()) out.ref_help = s.ref_help
  if (extraCapabilities.length) out.capabilities = [...new Set([...out.capabilities, ...extraCapabilities])].sort()
  return out
}

export interface EditorVocabulary {
  meta: ActionsMeta
  /** capability -> one line of meaning (the capability map's `vocabulary`). */
  meanings: Record<string, string>
}

/** The merged vocabulary for the editors (never throws; defaults while loading or offline). */
export function useActionsMeta(): EditorVocabulary {
  const meta = useQuery(actionsMetaQuery)
  const caps = useQuery(capabilitiesQuery)
  const capMap = caps.data?.capabilities
  return useMemo(() => {
    const vocab = capMap && typeof capMap.vocabulary === 'object' && capMap.vocabulary ? (capMap.vocabulary as Record<string, unknown>) : {}
    const merged = mergeMeta(meta.data, capMap ? mapCapabilities(capMap) : [])
    const meanings: Record<string, string> = { ...(merged.capability_help ?? {}) }
    for (const [k, v] of Object.entries(vocab)) if (typeof v === 'string') meanings[k] = v
    return { meta: merged, meanings }
  }, [meta.data, capMap])
}

/** The capability map's sections that ADD capabilities (`exclude` removes them). */
export const CAPABILITY_SECTIONS = ['categories', 'keywords', 'slugs', 'kinds', 'plans', 'types', 'clips', 'sockets', 'rest_surfaces', 'region_kinds', 'anchor_kinds', 'affords', 'exclude'] as const
export type CapabilitySection = (typeof CAPABILITY_SECTIONS)[number]

/** Every capability a map names: its sections' lists, its size rules and its vocabulary. */
export function mapCapabilities(map: Record<string, unknown>): string[] {
  const out = new Set<string>()
  for (const section of CAPABILITY_SECTIONS) {
    const table = map[section]
    if (table && typeof table === 'object' && !Array.isArray(table)) for (const caps of Object.values(table)) for (const c of strings(caps) ?? []) out.add(c)
  }
  if (Array.isArray(map.size)) for (const r of map.size) if (r && typeof (r as { capability?: unknown }).capability === 'string') out.add((r as { capability: string }).capability)
  if (map.vocabulary && typeof map.vocabulary === 'object') for (const k of Object.keys(map.vocabulary)) out.add(k)
  // A plain-text list may still carry the old "maybe" mark (`climbable?`).
  return [...new Set([...out].map((c) => c.replace(/\?$/, '')))].filter(Boolean).sort()
}
